import ConfirmModalUI from '@/components/common/ConfirmModalUI/ConfirmModalUI'
import { debounce } from 'lodash'
import { toast } from 'react-hot-toast'
import { useConfigUI } from '../../context'
import useDeleteArea from '../../hooks/areasandzones/useDeleteArea'
import useTranslation from '@/hooks/useTranslation'

interface DeleteModalProps {
  id?: number
  interfaceId?: string
  onSuccess?: () => void
  mode?: string
  name?: string
}

const DeleteAreaModal = ({
  id,
  interfaceId,
  onSuccess,
  mode = 'Area',
  name = '',
}: DeleteModalProps) => {
  const { t } = useTranslation()
  const { closeModal } = useConfigUI()

  // Callers spell this three different ways — 'subarea' from the grid, 'Sub
  // Area' from the area form, 'Area' as the default — and the old `mode ===
  // 'Area'` test matched none of the first two, so every row was called a
  // sub-area, including top-level areas. Match on the distinguishing word
  // instead of an exact string.
  const isSubArea = /sub/i.test(mode)
  const label = isSubArea ? t('subArea') : t('commonUnitsArea')
  // Deleting an area deletes its sub-areas too (the API cascades), so the
  // warning says so, as production's did: "Area, all sub-area data will be
  // deleted." The old lead-in ("You will delete user …") is not reused: it is
  // the user-delete sentence.
  const warning = isSubArea
    ? `${label} ${t('dataWillBeDeleted')}`
    : `${label}, ${t('allSubArea')} ${t('dataWillBeDeleted')}`

  const { mutate: deleteModal } = useDeleteArea()

  const handleDeleteArea = debounce(async () => {
    if (!id) return
    if (!interfaceId) return
    deleteModal(
      { interfaceId, id },
      {
        onSuccess: () => {
          onSuccess && onSuccess()
          toast.success(
            `${t('commonActionsDelete')} ${label} ${t('commonStatusSuccess')}`,
          )
        },
      },
    )
  }, 300)

  return (
    <ConfirmModalUI
      title={`${t('commonActionsDelete')} ${label}?`}
      name={name}
      text={warning}
      onClose={closeModal}
      onConfirm={handleDeleteArea}
    />
  )
}

export default DeleteAreaModal
